<app-section-quick-actions section="item" />

<header class="atmos-page__header">
  <h1 class="atmos-page__title">Item collections</h1>
</header>

<p class="atmos-paragraph">
  On this page you can save your own item collections. Use these to keep track of your favourite outfits, plan for upcoming items or anything else!
</p>

<div class="atmos-coll__actions">
  <button type="button" class="atmos-btn" (click)="toggleShowAdd()" [class.atmos-btn--active]="showEdit && !editGuid">
    <mat-icon>add</mat-icon> Add collection
  </button>
  <button type="button" class="atmos-btn" (click)="importCollectionFile()">
    <mat-icon>download</mat-icon> Import
  </button>
</div>

<!-- Add / Edit form -->
@if (showEdit) {
  <section class="atmos-card atmos-card__body">
    <h2 class="atmos-section__title">{{ editGuid ? 'Edit collection' : 'New collection' }}</h2>

    <form class="atmos-coll__form" [formGroup]="editForm">
      <div class="atmos-coll__form-fields">
        <label class="atmos-coll__label">
          Name
          <input class="atmos-input" type="text" maxlength="100" placeholder="Collection name" formControlName="name">
        </label>
        <label class="atmos-coll__label">
          Description
          <textarea class="atmos-input atmos-input--textarea" maxlength="2000" placeholder="Optional description" formControlName="description"></textarea>
        </label>
        <label class="atmos-coll__label">
          Image URL
          <input class="atmos-input" type="text" placeholder="https://…" formControlName="imageUrl">
          @if (editImgError) { <span class="atmos-coll__input-error">Please enter a valid https:// URL</span> }
        </label>
      </div>
      @if (editImgSrc) {
        <div class="atmos-coll__form-preview">
          <img [src]="editImgSrc" class="atmos-coll__preview-img" alt="Preview">
        </div>
      }
    </form>

    <!-- Selected items strip -->
    <div>
      <h3 class="atmos-coll__subsection-title">Selected items ({{ editItems.length }})</h3>
      <div class="atmos-coll__selected">
        @for (item of editItems; track item.guid) {
          <button type="button" class="atmos-coll__selected-item atmos-tile atmos-tile--removable" (click)="removeItem(item)" [attr.aria-label]="item.name" [skyTooltip]="item.name + ' — click to remove'">
            <app-item-icon [item]="item" size="medium" [subIcons]="SUBICONS_ALL"/>
            <span class="atmos-tile__remove">✕</span>
          </button>
        } @empty {
          <p class="atmos-coll__empty-hint">No items selected. Pick items from the grid below.</p>
        }
      </div>
    </div>

    <div class="atmos-coll__form-actions">
      <button type="button" class="atmos-btn atmos-btn--sm" (click)="saveCollection()">
        <mat-icon>save</mat-icon> {{ editGuid ? 'Save changes' : 'Save collection' }}
      </button>
      @if (editGuid) {
        <button type="button" class="atmos-btn atmos-btn--sm" (click)="cancelEdit()">
          <mat-icon>close</mat-icon> Cancel
        </button>
      }
    </div>
  </section>

  <!-- Item picker -->
  <app-item-grid-layout [items]="allItems" [itemTemplate]="pickerTpl" [initialCategory]="initialCategory"/>
  <ng-template #pickerTpl let-item>
    <button type="button"
            class="atmos-coll__picker-item atmos-tile atmos-tile--selectable"
            [class.is-selected]="editItemSet.has(item)"
            [skyTooltip]="item.name" [attr.aria-label]="item.name"
            (click)="onItemClickedPicker(item)">
      <app-item-icon [item]="item" size="medium" [subIcons]="SUBICONS_ALL"/>
      @if (editItemSet.has(item)) { <span class="atmos-tile__check">✓</span> }
    </button>
  </ng-template>

}

<!-- Collections list -->
<div [hidden]="showEdit" style="display: contents;">
  @if (collections.length === 0) {
    <section class="atmos-card atmos-card__body">
      <p class="atmos-coll__empty-hint">No collections yet. Click <strong>Add collection</strong> to get started.</p>
    </section>
  }

  @for (collection of collections; track collection.guid; let i = $index; let first = $first; let last = $last) {
    <section class="atmos-card" [attr.data-collection]="collection.guid">
      <div class="atmos-coll__row-header atmos-card__body">
        <div class="atmos-coll__row-info">
          @if (collection.imageUrl) {
            <img class="atmos-coll__thumb" [src]="collection.imageUrl" alt="">
          }
          <div class="atmos-coll__row-text">
            <span class="atmos-coll__row-name">{{ collection.name }}</span>
            @if (collection.description) {
              <span class="atmos-coll__row-desc">{{ collection.description }}</span>
            }
            <div class="atmos-coll__chips">
              <span class="atmos-pill">{{ collection.items.length }} items</span>
              <span class="atmos-pill atmos-pill--active">{{ collectionUnlockedCount(collection) }} unlocked</span>
            </div>
          </div>
        </div>

        <div class="atmos-coll__row-actions">
          <button type="button" class="atmos-btn atmos-btn--sm" [class.is-disabled]="first" (click)="moveCollectionUp(i)" skyTooltip="Move up" aria-label="Move up">
            <mat-icon>arrow_upward</mat-icon>
          </button>
          <button type="button" class="atmos-btn atmos-btn--sm" [class.is-disabled]="last" (click)="moveCollectionDown(i)" skyTooltip="Move down" aria-label="Move down">
            <mat-icon>arrow_downward</mat-icon>
          </button>
          <button type="button" class="atmos-btn atmos-btn--sm" (click)="showEditCollection(collection)" skyTooltip="Edit" aria-label="Edit">
            <mat-icon>edit</mat-icon>
          </button>
          <button type="button" class="atmos-btn atmos-btn--sm" (click)="gotoCalculator(collection)" skyTooltip="Calculate cost" aria-label="Calculate cost"
            [disabled]="!collection.items.length">
            <mat-icon>calculate</mat-icon>
          </button>
          <button type="button" class="atmos-btn atmos-btn--sm" (click)="showCopyCollection(collection)" skyTooltip="Copy" aria-label="Copy">
            <mat-icon>content_copy</mat-icon>
          </button>
          <button type="button" class="atmos-btn atmos-btn--sm" (click)="exportCollection(collection)" skyTooltip="Export" aria-label="Export">
            <mat-icon>upload</mat-icon>
          </button>
          <button type="button" class="atmos-btn atmos-btn--sm" (click)="promptDeleteCollection(collection)" skyTooltip="Delete" aria-label="Delete">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>

      @if (collection.items.length) {
        <div class="atmos-coll__items-strip atmos-card__body">
          @for (item of collection.items; track item.guid) {
            <a class="atmos-coll__strip-item atmos-tile" [routerLink]="['/item', item.guid]" [skyTooltip]="item.name" [attr.aria-label]="item.name">
              <app-item-icon [item]="item" size="medium" [subIcons]="SUBICONS_ALL"/>
            </a>
          }
        </div>
      }
    </section>
  }

  @if (collections.length) {
    <div class="atmos-coll__actions">
      <button type="button" class="atmos-btn" (click)="addCollectionFromBottom()">
        <mat-icon>add</mat-icon> Add collection
      </button>
    </div>
  }
</div>
